import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, FileText, LogOut } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";

// ============== ADMIN - COMMONLY REQUIRED ITEMS ==============
const AdminCommonlyRequiredItems = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRule, setSelectedRule] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/app/login");
  };

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/admin/commonly-required-rules`);
      setRules(res.data);
    } catch (err) {
      console.error("Failed to fetch rules:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleSaveRule = async () => {
    if (!editingRule) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/commonly-required-rules/${editingRule.rule_id}`, {
        display_name: editingRule.display_name,
        keywords: editingRule.keywords,
        suggestions: editingRule.suggestions
      });
      await fetchRules();
      setEditingRule(null);
      setSelectedRule(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRule = async (newRule) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/commonly-required-rules`, newRule);
      await fetchRules();
      setShowCreateModal(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm(`Are you sure you want to delete the "${ruleId}" rule? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/commonly-required-rules/${ruleId}`);
      await fetchRules();
      setSelectedRule(null);
      setEditingRule(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete rule");
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm("This will reset ALL rules to their default values. Any custom rules or changes will be lost. Continue?")) return;
    try {
      await axios.post(`${API}/admin/commonly-required-rules/reset`);
      await fetchRules();
      setSelectedRule(null);
      setEditingRule(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reset rules");
    }
  };

  const startEditing = (rule) => {
    setEditingRule(JSON.parse(JSON.stringify(rule))); // Deep copy
  };

  const addKeyword = () => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      keywords: [...editingRule.keywords, ""]
    });
  };

  const updateKeyword = (index, value) => {
    if (!editingRule) return;
    const newKeywords = [...editingRule.keywords];
    newKeywords[index] = value;
    setEditingRule({ ...editingRule, keywords: newKeywords });
  };

  const removeKeyword = (index) => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      keywords: editingRule.keywords.filter((_, i) => i !== index)
    });
  };

  const addSuggestion = () => {
    if (!editingRule) return;
    const newId = `${editingRule.rule_id}-${Date.now()}`;
    setEditingRule({
      ...editingRule,
      suggestions: [...editingRule.suggestions, { id: newId, name: "", category: "parts", reason: "" }]
    });
  };

  const updateSuggestion = (index, field, value) => {
    if (!editingRule) return;
    const newSuggestions = [...editingRule.suggestions];
    newSuggestions[index] = { ...newSuggestions[index], [field]: value };
    setEditingRule({ ...editingRule, suggestions: newSuggestions });
  };

  const removeSuggestion = (index) => {
    if (!editingRule) return;
    setEditingRule({
      ...editingRule,
      suggestions: editingRule.suggestions.filter((_, i) => i !== index)
    });
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <img src="/yestimate-logo.jpeg" alt="Yestimate" className="h-10 w-auto" style={{ maxHeight: '40px' }} />
            <p className="text-xs text-slate-500">Admin Panel</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/app/foh")} data-testid="back-to-foh-btn">
              Back to Service Advisor
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {/* Page Title & Actions */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="admin-title">Commonly Required Items</h1>
            <p className="text-slate-500">Manage suggestion rules that appear when creating job groups</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleResetToDefaults} data-testid="reset-defaults-btn">
              Reset to Defaults
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-rule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Rule Category
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Rules List */}
          <div className="col-span-1">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Rule Categories</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {rules.map(rule => (
                    <div
                      key={rule.rule_id}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${selectedRule?.rule_id === rule.rule_id ? 'bg-blue-50 border-l-4 border-blue-600' : ''}`}
                      onClick={() => { setSelectedRule(rule); setEditingRule(null); }}
                      data-testid={`rule-item-${rule.rule_id}`}
                    >
                      <div className="font-medium text-slate-800">{rule.display_name}</div>
                      <div className="text-sm text-slate-500 mt-1">
                        {rule.keywords.length} keywords · {rule.suggestions.length} suggestions
                      </div>
                    </div>
                  ))}
                  {rules.length === 0 && (
                    <div className="p-4 text-center text-slate-500">
                      No rules configured
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Rule Detail / Editor */}
          <div className="col-span-2">
            {!selectedRule && !editingRule && (
              <Card>
                <CardContent className="p-12 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a rule category to view or edit</p>
                </CardContent>
              </Card>
            )}

            {selectedRule && !editingRule && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{selectedRule.display_name}</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">Rule ID: {selectedRule.rule_id}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => startEditing(selectedRule)} data-testid="edit-rule-btn">
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteRule(selectedRule.rule_id)} data-testid="delete-rule-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Keywords */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Trigger Keywords</h3>
                    <p className="text-xs text-slate-500 mb-2">These keywords in job titles will trigger suggestions</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedRule.keywords.map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="px-3 py-1">{kw}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Suggestions */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Suggested Items</h3>
                    <p className="text-xs text-slate-500 mb-3">Items suggested when keywords match</p>
                    <div className="space-y-3">
                      {selectedRule.suggestions.map((s, idx) => (
                        <div key={idx} className="p-3 border rounded-lg bg-slate-50">
                          <div className="flex justify-between items-start">
                            <div className="font-medium text-slate-800">{s.name}</div>
                            <Badge variant={s.category === 'parts' ? 'default' : s.category === 'labour' ? 'outline' : 'secondary'}>
                              {s.category}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{s.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {editingRule && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">Editing: {editingRule.display_name}</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">Rule ID: {editingRule.rule_id}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingRule(null)} data-testid="cancel-edit-btn">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveRule} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-rule-btn">
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Display Name */}
                  <div className="mb-6">
                    <Label className="text-sm font-semibold text-slate-700">Display Name</Label>
                    <Input
                      value={editingRule.display_name}
                      onChange={(e) => setEditingRule({ ...editingRule, display_name: e.target.value })}
                      className="mt-1"
                      data-testid="edit-display-name"
                    />
                  </div>

                  {/* Keywords Editor */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-sm font-semibold text-slate-700">Trigger Keywords</Label>
                      <Button variant="outline" size="sm" onClick={addKeyword} data-testid="add-keyword-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Job title keywords that will trigger these suggestions</p>
                    <div className="space-y-2">
                      {editingRule.keywords.map((kw, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={kw}
                            onChange={(e) => updateKeyword(idx, e.target.value)}
                            placeholder="Enter keyword..."
                            className="flex-1"
                            data-testid={`keyword-input-${idx}`}
                          />
                          <Button variant="ghost" size="sm" onClick={() => removeKeyword(idx)} className="text-red-500 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Suggestions Editor */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label className="text-sm font-semibold text-slate-700">Suggested Items</Label>
                      <Button variant="outline" size="sm" onClick={addSuggestion} data-testid="add-suggestion-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add Item
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">Items to suggest when keywords match</p>
                    <div className="space-y-4">
                      {editingRule.suggestions.map((s, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-slate-50">
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-xs text-slate-400">Item #{idx + 1}</span>
                            <Button variant="ghost" size="sm" onClick={() => removeSuggestion(idx)} className="text-red-500 hover:bg-red-50 -mt-1 -mr-2">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Item Name</Label>
                              <Input
                                value={s.name}
                                onChange={(e) => updateSuggestion(idx, 'name', e.target.value)}
                                placeholder="e.g., Brake Fitting Kit"
                                className="mt-1"
                                data-testid={`suggestion-name-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Category</Label>
                              <Select value={s.category} onValueChange={(v) => updateSuggestion(idx, 'category', v)}>
                                <SelectTrigger className="mt-1" data-testid={`suggestion-category-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="parts">Parts</SelectItem>
                                  <SelectItem value="labour">Labour</SelectItem>
                                  <SelectItem value="consumables">Consumables</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Reason / Description</Label>
                              <Input
                                value={s.reason}
                                onChange={(e) => updateSuggestion(idx, 'reason', e.target.value)}
                                placeholder="e.g., Often required for brake component replacement"
                                className="mt-1"
                                data-testid={`suggestion-reason-${idx}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Rule Modal */}
      {showCreateModal && (
        <CreateRuleModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateRule}
          saving={saving}
        />
      )}
    </div>
  );
};

// Create Rule Modal Component
const CreateRuleModal = ({ onClose, onSave, saving }) => {
  const [newRule, setNewRule] = useState({
    rule_id: "",
    display_name: "",
    keywords: [""],
    suggestions: [{ id: "", name: "", category: "parts", reason: "" }]
  });

  const handleSubmit = () => {
    if (!newRule.rule_id.trim() || !newRule.display_name.trim()) {
      alert("Rule ID and Display Name are required");
      return;
    }
    if (newRule.keywords.filter(k => k.trim()).length === 0) {
      alert("At least one keyword is required");
      return;
    }
    if (newRule.suggestions.filter(s => s.name.trim()).length === 0) {
      alert("At least one suggestion is required");
      return;
    }

    // Clean up and generate IDs
    const cleanedRule = {
      rule_id: newRule.rule_id.toLowerCase().replace(/\s+/g, '_'),
      display_name: newRule.display_name,
      keywords: newRule.keywords.filter(k => k.trim()),
      suggestions: newRule.suggestions.filter(s => s.name.trim()).map((s, idx) => ({
        ...s,
        id: s.id || `${newRule.rule_id.toLowerCase().replace(/\s+/g, '_')}-${idx + 1}`
      }))
    };

    onSave(cleanedRule);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-rule-modal">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Create New Rule Category</CardTitle>
          <CardDescription>Add a new category for commonly required items suggestions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rule ID</Label>
                <Input
                  value={newRule.rule_id}
                  onChange={(e) => setNewRule({ ...newRule, rule_id: e.target.value })}
                  placeholder="e.g., tyres"
                  className="mt-1"
                  data-testid="new-rule-id"
                />
                <p className="text-xs text-slate-500 mt-1">Lowercase, no spaces (used internally)</p>
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  value={newRule.display_name}
                  onChange={(e) => setNewRule({ ...newRule, display_name: e.target.value })}
                  placeholder="e.g., Tyres"
                  className="mt-1"
                  data-testid="new-display-name"
                />
              </div>
            </div>

            <div>
              <Label>Keywords (one per line)</Label>
              <Textarea
                value={newRule.keywords.join('\n')}
                onChange={(e) => setNewRule({ ...newRule, keywords: e.target.value.split('\n') })}
                placeholder="tyre&#10;wheel&#10;puncture"
                className="mt-1"
                rows={3}
                data-testid="new-keywords"
              />
            </div>

            <div>
              <Label>Initial Suggestion</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  value={newRule.suggestions[0].name}
                  onChange={(e) => setNewRule({
                    ...newRule,
                    suggestions: [{ ...newRule.suggestions[0], name: e.target.value }]
                  })}
                  placeholder="Item name"
                  data-testid="new-suggestion-name"
                />
                <Select
                  value={newRule.suggestions[0].category}
                  onValueChange={(v) => setNewRule({
                    ...newRule,
                    suggestions: [{ ...newRule.suggestions[0], category: v }]
                  })}
                >
                  <SelectTrigger data-testid="new-suggestion-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parts">Parts</SelectItem>
                    <SelectItem value="labour">Labour</SelectItem>
                    <SelectItem value="consumables">Consumables</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                value={newRule.suggestions[0].reason}
                onChange={(e) => setNewRule({
                  ...newRule,
                  suggestions: [{ ...newRule.suggestions[0], reason: e.target.value }]
                })}
                placeholder="Reason / Description"
                className="mt-2"
                data-testid="new-suggestion-reason"
              />
            </div>
          </div>
        </CardContent>
        <div className="flex justify-end gap-2 p-6 pt-0">
          <Button variant="outline" onClick={onClose} data-testid="cancel-create-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-create-btn">
            {saving ? "Creating..." : "Create Rule"}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminCommonlyRequiredItems;
